import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography } from '../../../base-components';
import { IUploadProps, CustomFile } from '../FormUploadFile.interfaces';
import {
    StyledDropZone,
    PreviewContainer,
    ImagePreview,
    ActionButtons,
    ActionButton,
    PDFPreview,
    FileInfo,
} from './UploadImage.styles';
import Placeholder from '../UploadFile/Placeholder';
import ErrorFiles from '../Previews/ErrorFile';
import ImageEditor from '../ImageEditor';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplaceIcon from '@mui/icons-material/Autorenew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useTheme } from '@mui/material/styles';
import {
    isImageFile,
    isPdfFile,
    formatFileSize,
    compressImage,
    getImageOutputFormat,
    ImageOutputFormat,
} from '../utils/imageProcessing.utils';
import { useTranslation } from 'react-i18next';

interface UploadImageProps extends IUploadProps {
    onDrop: (files: File[]) => void;
    onDelete?: () => void;
    enableEditor?: boolean;
    initialAspectRatio?: number;
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
}

const UploadImage = (props: UploadImageProps) => {
    const {
        disabled,
        error,
        helperText,
        file,
        onDrop,
        onDelete,
        enableEditor = true,
        initialAspectRatio,
        maxWidth = 1920,
        maxHeight = 1080,
        quality = 0.9,
        sx,
        ...other
    } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const [editorOpen, setEditorOpen] = useState(false);
    const [tempImageForEdit, setTempImageForEdit] = useState<string | null>(
        null
    );
    const [outputFormat, setOutputFormat] =
        useState<ImageOutputFormat>('image/jpeg');

    const handleDropInternal = useCallback(
        async (acceptedFiles: File[]) => {
            if (acceptedFiles.length === 0) return;

            const droppedFile = acceptedFiles[0];

            // If it's a PDF, just pass it through
            if (isPdfFile(droppedFile)) {
                onDrop(acceptedFiles);
                return;
            }

            // If it's an image, compress it first
            if (isImageFile(droppedFile)) {
                try {
                    const imageOutputFormat = getImageOutputFormat(droppedFile);
                    setOutputFormat(imageOutputFormat);

                    const { file: compressedFile, url } = await compressImage(
                        droppedFile,
                        {
                            maxWidth,
                            maxHeight,
                            quality,
                            outputFormat: imageOutputFormat,
                        }
                    );

                    // Open editor if enabled
                    if (enableEditor) {
                        setTempImageForEdit(url);
                        setEditorOpen(true);
                    } else {
                        // Otherwise, just use the compressed image
                        const fileWithPreview = Object.assign(compressedFile, {
                            preview: url,
                        });
                        onDrop([fileWithPreview]);
                    }
                } catch (error) {
                    console.error('Error compressing image:', error);
                    // Fallback to original file
                    onDrop(acceptedFiles);
                }
            } else {
                onDrop(acceptedFiles);
            }
        },
        [onDrop, enableEditor, maxWidth, maxHeight, quality]
    );

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragReject,
        fileRejections,
    } = useDropzone({
        multiple: false,
        disabled: disabled || !!file,
        onDrop: handleDropInternal,
        ...other,
    });

    const hasFile = !!file;
    const isError = isDragReject || !!error;

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!file) return;

        const fileUrl =
            typeof file === 'string'
                ? file
                : (file as CustomFile).preview || URL.createObjectURL(file);
        setOutputFormat(getImageOutputFormat(file));
        setTempImageForEdit(fileUrl);
        setEditorOpen(true);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDelete) {
            onDelete();
        }
    };

    const handleReplace = () => {
        // Trigger file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,application/pdf';
        input.onchange = (e) => {
            const files = (e.target as HTMLInputElement).files;
            if (files && files.length > 0) {
                handleDropInternal([files[0]]);
            }
        };
        input.click();
    };

    const handleEditorSave = (editedFile: File, url: string) => {
        const fileWithPreview = Object.assign(editedFile, {
            preview: url,
        });
        onDrop([fileWithPreview]);
        setEditorOpen(false);
        setTempImageForEdit(null);
    };

    const handleEditorClose = () => {
        setEditorOpen(false);
        if (tempImageForEdit && !file) {
            URL.revokeObjectURL(tempImageForEdit);
        }
        setTempImageForEdit(null);
    };

    const getFilePreview = () => {
        if (!file) return null;

        if (typeof file === 'string') {
            return file;
        }

        return (file as CustomFile).preview || URL.createObjectURL(file);
    };

    const renderContent = () => {
        if (hasFile) {
            const fileUrl = getFilePreview();
            const fileObj = typeof file === 'string' ? null : file;

            // Check if it's a PDF
            if (
                (fileObj && isPdfFile(fileObj)) ||
                (typeof file === 'string' && isPdfFile(file))
            ) {
                return (
                    <PreviewContainer>
                        <ActionButtons>
                            <ActionButton onClick={handleReplace}>
                                <ReplaceIcon fontSize="small" />
                            </ActionButton>
                            {onDelete && (
                                <ActionButton onClick={handleDelete}>
                                    <DeleteIcon
                                        fontSize="small"
                                        color="error"
                                    />
                                </ActionButton>
                            )}
                        </ActionButtons>
                        <PDFPreview>
                            <PictureAsPdfIcon
                                sx={{ fontSize: 64 }}
                                color="error"
                            />
                            <FileInfo>
                                <Typography variant="subtitle2">
                                    {fileObj?.name || 'PDF Document'}
                                </Typography>
                                {fileObj && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {formatFileSize(fileObj.size)}
                                    </Typography>
                                )}
                            </FileInfo>
                        </PDFPreview>
                    </PreviewContainer>
                );
            }

            // It's an image
            return (
                <PreviewContainer>
                    <ActionButtons>
                        {enableEditor && (
                            <ActionButton onClick={handleEdit}>
                                <EditIcon fontSize="small" />
                            </ActionButton>
                        )}
                        <ActionButton onClick={handleReplace}>
                            <ReplaceIcon fontSize="small" />
                        </ActionButton>
                        {onDelete && (
                            <ActionButton onClick={handleDelete}>
                                <DeleteIcon fontSize="small" color="error" />
                            </ActionButton>
                        )}
                    </ActionButtons>
                    <ImagePreview src={fileUrl || ''} alt="Preview" />
                </PreviewContainer>
            );
        }

        return (
            <StyledDropZone
                {...getRootProps()}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.light,
                    border: `1px dashed ${theme.palette.primary.main}`,
                    padding: theme.spacing(1.5),
                    ...(isDragActive && {
                        opacity: 0.72,
                    }),
                    ...(isError && {
                        color: 'error.main',
                        bgcolor: 'error.light',
                        borderColor: 'error.light',
                    }),
                    ...(disabled && {
                        opacity: 0.48,
                        pointerEvents: 'none',
                    }),
                }}
            >
                <input {...getInputProps()} accept="image/*,application/pdf" />
                <Placeholder />
            </StyledDropZone>
        );
    };

    return (
        <Box sx={{ width: 1, position: 'relative', ...sx }}>
            {renderContent()}

            {helperText && helperText}
            <ErrorFiles fileRejections={fileRejections} />

            {tempImageForEdit && (
                <ImageEditor
                    open={editorOpen}
                    imageSrc={tempImageForEdit}
                    onClose={handleEditorClose}
                    onSave={handleEditorSave}
                    initialAspectRatio={initialAspectRatio}
                    maxWidth={maxWidth}
                    maxHeight={maxHeight}
                    quality={quality}
                    outputFormat={outputFormat}
                />
            )}
        </Box>
    );
};

export default UploadImage;
